import { Route } from 'react-router';
import { Routes, useLocation } from 'react-router';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter } from 'react-router';
import Protected from './auth/Protected';
import PlatformContainer from './components/layout/PlatformContainer';
import { usePageViewTracking } from './hooks/usePageViewTracking';

import { useFeatureAccess } from '@/api/user/useUserQuery';
import LoadingScreen from './components/LoadingScreen';
import FeatureGate from './components/FeatureGate';

function PageViewTracker() {
  usePageViewTracking();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const Home = lazy(() => import('./pages/home'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const AreaReportTable = lazy(() => import('./pages/area-report'));
const AreaReportView = lazy(() => import('./pages/area-report-view'));
const AreaGroups = lazy(() => import('./pages/area-groups/area-groups'));
const AreaGroupView = lazy(() => import('./pages/area-groups/area-group-view'));

const Portfolios = lazy(() => import('./pages/portfolios'));
const PortfolioView = lazy(() => import('./pages/portfolio-view'));
const Properties = lazy(() => import('./pages/properties-view'));
const PropertyView = lazy(() => import('./pages/property-view'));
const Admin = lazy(() => import('./pages/admin'));
const AdminOrganization = lazy(() => import('./pages/admin-organization'));
const AdminCreateOrganization = lazy(
  () => import('./pages/admin-create-organization')
);
const Settings = lazy(() => import('./pages/settings'));

function Router() {
  const featureAccess = useFeatureAccess();

  const hasAreaAnalysis = featureAccess?.hasAreaAnalysis !== false;
  const hasPropertyAnalysis = featureAccess?.hasPropertyAnalysis !== false;

  return (
    <BrowserRouter>
      <PageViewTracker />
      <ScrollToTop />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/area-report"
            element={
              <Protected>
                <PlatformContainer>
                  {hasAreaAnalysis ? <AreaReportTable /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />
          <Route
            path="/area-report/:reportId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasAreaAnalysis ? <AreaReportView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/area-portfolios"
            element={
              <Protected>
                <PlatformContainer>
                  {hasAreaAnalysis ? <AreaGroups /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/area-portfolios/:areaGroupId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasAreaAnalysis ? <AreaGroupView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/area-portfolios/:areaGroupId/:reportId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasAreaAnalysis ? <AreaReportView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />
          <Route
            path="/admin"
            element={
              <Protected requireSuperuser>
                <PlatformContainer>
                  <Admin />
                </PlatformContainer>
              </Protected>
            }
          />
          <Route
            path="/admin/create-organization"
            element={
              <Protected requireSuperuser>
                <PlatformContainer>
                  <AdminCreateOrganization />
                </PlatformContainer>
              </Protected>
            }
          />
          <Route
            path="/admin/:organizationId"
            element={
              <Protected requireSuperuser>
                <PlatformContainer>
                  <AdminOrganization />
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/portfolios"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <Portfolios /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/portfolios/:portfolioName/:portfolioId/:propertyName/:propertyId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <PropertyView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/portfolios/:portfolioName/:portfolioId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <PortfolioView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/portfolios/:portfolioId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <PortfolioView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/properties"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <Properties /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/properties/:propertyId"
            element={
              <Protected>
                <PlatformContainer>
                  {hasPropertyAnalysis ? <PropertyView /> : <FeatureGate />}
                </PlatformContainer>
              </Protected>
            }
          />

          <Route
            path="/settings"
            element={
              <Protected>
                <PlatformContainer>
                  <Settings />
                </PlatformContainer>
              </Protected>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default Router;
